import { MenuOutlined } from '@ant-design/icons'
import { Layout, Button, Space, Typography } from 'antd'
import React, { useState, useEffect } from 'react'

import Breadcrumb from './Breadcrumb'
import NotificationBell from './NotificationBell'
import ThemeToggle from './ThemeToggle'
import UserMenu from './UserMenu'

import { TRANSITIONS } from '@/shared/constants/transitions'
import { useThemeColors } from '@/shared/hooks/useThemeColors'
import { TopBarProps } from '@/types/layout'
import { Z_INDEX } from '@/types/layout'

const { Header } = Layout
const { Title } = Typography

export const TopBar: React.FC<TopBarProps> = ({
  breadcrumbs,
  pageTitle,
  user,
  notifications,
  sidebarCollapsed,
  onToggleMobileMenu,
}) => {
  const colors = useThemeColors()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <Header
      style={{ 
        position: 'sticky',
        top: isMobile ? 0 : 24,
        zIndex: Z_INDEX.TOPBAR,
        backgroundColor: colors.isDark ? '#0f2929' : '#FFFFFF',
        border: 'none',
        height: '72px',
        lineHeight: 'normal',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        transition: TRANSITIONS.THEME,
        borderRadius: isMobile ? '0' : '16px',
        boxShadow: isMobile ? 'none' : colors.isDark 
          ? '0 4px 24px rgba(0, 0, 0, 0.4)'
          : '0 4px 24px rgba(0, 0, 0, 0.08)',
      }}
    >
      {/* Left Section: Hamburger (mobile), Breadcrumb & Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
        {/* Hamburger Menu (Mobile Only) */}
        <div className="md:hidden" style={{ flexShrink: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 22 }} />}
            onClick={onToggleMobileMenu}
            shape="circle"
            size="large"
            style={{ 
              color: colors.textSecondary,
              transition: TRANSITIONS.FAST,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLElement
              target.style.color = colors.primaryHover
              target.style.backgroundColor = colors.backgroundHover
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLElement
              target.style.color = colors.textSecondary
              target.style.backgroundColor = 'transparent'
            }}
            aria-label="Toggle navigation menu"
          />
        </div>

        {/* Desktop: Breadcrumb + Page Title */}
        <div className="hidden md:flex" style={{ flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <Breadcrumb segments={breadcrumbs} />
          {pageTitle && (
            <Title 
              level={4} 
              style={{ 
                margin: 0,
                color: colors.textPrimary,
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '28px',
                transition: TRANSITIONS.COLOR
              }}
              ellipsis
            >
              {pageTitle}
            </Title>
          )}
        </div>

        {/* Mobile: Page Title only */}
        {pageTitle && (
          <Title 
            level={5}
            className="md:hidden"
            style={{ 
              margin: 0,
              color: colors.textPrimary,
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: 'normal',
              flex: 1,
              minWidth: 0,
              transition: TRANSITIONS.COLOR
            }}
            ellipsis
          >
            {pageTitle}
          </Title>
        )}
      </div>

      {/* Right Section: Theme Toggle, Notifications, User Menu */}
      <Space size={12} style={{ flexShrink: 0 }}>
        <ThemeToggle />
        {/* <NotificationBell notifications={notifications} /> */}
        <UserMenu user={user} menuItems={[]} />
      </Space>
    </Header>
  )
}

export default TopBar
